import { useState, useMemo, useEffect, useContext, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Modal,
  Typography,
  Box,
  Button,
  FormControl,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { useStyles } from "../../Styles/styles";
import { createPersonFromScan, licenseParser } from "../../utils/LicenseParser";
import { PEOPLE_SEARCH_QUERY } from "../../Queries/PeopleSearchQuery";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../../Mutations/IncidentMutations/UpdatePersonInIncident";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { ADD_OR_UPDATE_PERSON } from "../../Mutations/AddOrUpdatePerson";
import {
  isJuvenile,
  handleUuid,
  formatName,
  personAlertColor,
  isAddressComplete,
  userHasSwitchCreds,
} from "../../utils/utils";
import { SetupContext } from "../../Contexts/SetupContext";
import _ from "lodash";
import { ADDRESS_AUTO_SUGGEST } from "../../Queries/AddressAutoSuggest";
import { ADD_OR_UPDATE_ADDRESS } from "../../Mutations/AddOrUpdateAddress";
import { SEND_QUERY_REQUEST } from "../../Mutations/SendQueryRequest";
import { checkRoles } from "../../utils/Roles";
import { ADD_AUDIT_LOG } from "../../Mutations/AddAuditLog";
import { createEmptyPersonIncidentSpecific } from "../../utils/EmptyObjects";
import { findOrCreateAddress as findOrCreateAddressUtil } from "../../utils/licenseScanUtils";
import HelpIconButton from "../HelpIconButton";
import SingleDropDown from "../SingleDropDown";
import { throttleFunction } from "../../utils/throttleFunction";
import ScanInput from "../ScanInput/ScanInput";
import { UserContext } from "../../Contexts/UserContext";

import moment from "moment";

const PROVINCES = [
  "AB",
  "BC",
  "MB",
  "NK",
  "NF",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "PQ",
  "SN",
  "YT",
];

//matched to Valcour eye color names
const EYE_COLOR_CODES = {
  BRO: "Brown",
  BLU: "Blue",
  HAZ: "Hazel",
  BLK: "Black",
  GRN: "Green",
  GRY: "Grey",
  DIC: "Multicolored",
  MAR: "Other",
  PNK: "Other",
  UNK: "Unknown",
  // "DIC": "Dichromatic",
  // "MAR": "Maroon",
  // "PNK": "Pink",
};

const HAIR_COLOR_CODES = {
  BAL: "Bald / XXX",
  BLK: "Black / BLK",
  BLN: "Blond or Strawberry / BLN",
  BRO: "Brown / BRO",
  GRY: "Grey or Partially Grey / GRY",
  RED: "Red/Auburn / RED",
  SDY: "Sandy / SDY",
  WHI: "White / WHI",
  UNK: "Unknown",
};

const RACE_CODES = {
  W: "White - W",
  BK: "Black - B",
  AI: "Native Amer/Alaska Nat - I",
  AP: "Asian/Pac Island - A",
  O: "Other - U",
  H: "Hispanic - U",
  U: "Unknown - U",
};

const ADDRESS_CODES = {
  AVENUE: "Ave",
  Boulevard: "Blvd",
  Circle: "Cir",
  Court: "Ct",
  Crossing: "Xing",
  Drive: "Dr",
  Estate: "Est",
  Extension: "Ext",
  Heights: "Hts",
  Highway: "Hwy",
  Lane: "Ln",
  Parkway: "Pkwy",
  Place: "Pl",
  Ridge: "Rdg",
  Road: "Rd",
  Street: "St",
  Terrace: "Ter",
  View: "Vw",
  Way: "Way",
};

const GENDER_CODES = {
  1: "Male",
  2: "Female",
  9: "Not specified",
};
const GENDER_TO_ID = {
  Male: 1,
  Female: 2,
};

const formatDob = (dob, country) => {
  if (dob && country) {
    let year;
    let month;
    let day;
    if (country === "CANADA") {
      //CCYYMMDD
      year = dob.slice(0, 4);
      month = dob.slice(4, 6);
      day = dob.slice(6, 8);
    } else {
      //USA and Mexico
      //MMDDCCYY
      //old license from FL has YYYYMMDD
      if (+dob.slice(0, 2) > 12) {
        year = dob.slice(0, 4);
        month = dob.slice(4, 6);
        day = dob.slice(6, 8);
      } else {
        year = dob.slice(4, 8);
        month = dob.slice(0, 2);
        day = dob.slice(2, 4);
      }
    }
    return [year, month, day].join("-");
  } else {
    return null;
  }
};

const convertWeight = (weight, country) => {
  if (weight && country) {
    if (country === "USA") {
      return +weight;
    } else {
      return Math.round(+weight * 2.20462);
    }
  } else {
    return 0;
  }
};

const convertHeight = (height, forDisplay = false) => {
  if (height) {
    const unit = _.lowerCase(height.slice(4, 6));
    const number = +height.slice(0, 3);
    if (unit === "in" || unit === "cm" || (!unit && number < 100)) {
      const convertedValue =
        _.lowerCase(unit) === "in" || !unit
          ? number
          : Math.round(number * 0.393701);
      const ft = Math.floor(convertedValue / 12);
      const inches = (convertedValue % 12).toString().padStart(2, "0");
      if (forDisplay) {
        return `${ft}' ${inches}"`;
      } else {
        return +[ft, inches].join("");
      }
    } else {
      //it's already in ft and inches eg 503 for 5'03"
      //one more check because DC sends in DAU074 without units (e.g. ' in')
      //if the height has no units and is greater than 100, it's all set, otherwise it needs to be convert
      if (forDisplay) {
        return `${height.charAt(0)}' ${height.slice(1)}"`;
      } else {
        return +height;
      }
    }
  } else {
    return 0;
  }
};

const formatAddressForAutoSuggest = (street, city, state, zip) => {
  const streetArr = street?.split(" ");
  const capitalize = streetArr?.map(i => _.capitalize(i));
  const abbrevReplace = capitalize?.map(i => ADDRESS_CODES[i] || i);
  const formattedStreet = abbrevReplace?.join(" ");

  return (
    formattedStreet?.trim() +
    ", " +
    _.capitalize(city)?.trim() +
    ", " +
    state?.trim() +
    ", " +
    zip?.slice(0, 5)
  );
};

const returnEmptyPis = piss => {
  let emptyPis = piss?.find(pis => !pis?.person?.personalAttribute?.lastName);
  if (emptyPis) {
    return emptyPis;
  } else {
    const pisUuid = handleUuid();
    const personUuid = handleUuid();
    const personalAttributeUuid = handleUuid();
    return createEmptyPersonIncidentSpecific(
      pisUuid,
      personUuid,
      personalAttributeUuid,
    );
  }
};

function formatDobForSearch(dob) {
  if (+dob.slice(0, 2) > 12) {
    return dob.slice(0, 4) + "-" + dob.slice(4, 6) + "-" + dob.slice(6, 8);
  } else {
    return dob.slice(4) + "-" + dob.slice(0, 2) + "-" + dob.slice(2, 4);
  }
}

function formatDobForDisplay(dob) {
  if (dob) {
    if (+dob.slice(0, 2) > 12) {
      return dob.slice(4, 6) + "/" + dob.slice(6, 8) + "/" + dob.slice(0, 4);
    } else {
      return dob.slice(0, 2) + "/" + dob.slice(2, 4) + "/" + dob.slice(4);
    }
  } else {
    return "";
  }
}

const LicenseScanModal = ({
  modalOpen,
  setModalOpen,
  user,
  incidentId,
  incident,
  nightMode,
  onlineState,
  setPopper,
  incidentPeople = [],
}) => {
  const classes = useStyles();
  // This styling will be updated in a later refactor
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedClasses = useMemo(() => classes, []);

  const { setup } = useContext(SetupContext);
  const { setupLists } = setup || {};
  const {
    races,
    hairColors,
    eyeColors,
    states,
    ibrArresteeRaceTypes,
    genders,
    nibrsGenders,
  } = setupLists || [];

  const { incidentDispatch } = useContext(IncidentsContext);
  const { tokenDisabled } = useContext(UserContext);
  const { ADD_OR_UPDATE_PIS } = INCIDENT_ACTIONS;

  let emptyPis = returnEmptyPis(incident.personIncidentSpecifics);
  const [data, setData] = useState("");
  const [personObj, setPersonObj] = useState(null);
  const [showResults, setShowResults] = useState(true);
  const [isParsing, setIsParsing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [mergeChoice, setMergeChoice] = useState({});
  const [reasonCode, setReasonCode] = useState(null);
  const [propInSight, setPropInSight] = useState(null);
  const [wait, setWait] = useState(false);
  const [combinedPersonSearchResults, setCombinedPersonSearchResults] =
    useState(null);

  const dashRef = useRef(null);
  const canRunQuery = useMemo(
    () =>
      checkRoles(user?.roleNames, "add_query_returns") &&
      userHasSwitchCreds(user),
    [user],
  );

  const { refetch: getAddressAutoSuggest } = useQuery(ADDRESS_AUTO_SUGGEST, {
    context: { serializationKey: "incidentQueue" },
    skip: true,
  });

  const [addOrUpdatePersonInIncident] = useMutation(
    ADD_OR_UPDATE_PERSON_IN_INCIDENT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: data => {
        const response = data?.[Object.keys(data)[0]];
        if (response?.personIncidentSpecific) {
          incidentDispatch({
            type: ADD_OR_UPDATE_PIS,
            payload: {
              incidentId: incidentId,
              pisUuid: response.personIncidentSpecificUuid,
              pis: response.personIncidentSpecific,
            },
          });
        }
      },
    },
  );
  const [addOrUpdatePerson] = useMutation(ADD_OR_UPDATE_PERSON, {
    context: { serializationKey: "incidentQueue" },
  });

  const throttledAddOrUpdatePerson = throttleFunction(addOrUpdatePerson);

  const [addOrUpdateAddress] = useMutation(ADD_OR_UPDATE_ADDRESS, {
    context: { serializationKey: "incidentQueue" },
  });

  const throttledAddOrUpdateAddress = throttleFunction(addOrUpdateAddress);

  const [sendQueryRequest] = useMutation(SEND_QUERY_REQUEST, {
    onCompleted: data => {
      if (data) {
        const res = data[Object.keys(data)[0]];
        if (res.message) {
          // alert(res.message)
          setPopper({ open: true, content: res.message, severity: "success" });
        } else {
          const errorMessage = res.errors.join(" ");
          // alert(errorMessage)
          setPopper({ open: true, content: errorMessage, severity: "error" });
        }
      }
    },
    onError: err => {
      // alert(err)
      setPopper({ open: true, content: err?.message, severity: "error" });
    },
  });

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG, {
  });

  const [parsingError, setParsingError] = useState(false);

  const handleSubmit = async event => {
    event?.preventDefault();
    setIsParsing(true);
    setSubmitted(true);
    let person = null;
    let validScan = false;
    try {
      const { federalArr, iin } = await licenseParser(data.toString());
      person = createPersonFromScan(federalArr, iin);
    } catch (error) {
      setParsingError(true);
      console.error(error);
    }
    if (validPerson(person)) {
      validScan = true;
      setPersonObj(person);
    } else {
      setParsingError(true);
      setPersonObj(person);
    }
    addAuditLog({
      variables: {
        input: {
          incidentId: incidentId,
          originatorId: user?.id,
          message: data,
          action: validScan ? "Successful Scan" : "Failed Scan",
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });

    setIsParsing(false);
  };

  const findOrCreateAddress = async addressString =>
    findOrCreateAddressUtil({
      addressString,
      onlineState,
      userId: user?.id,
      incidentId,
      getAddressAutoSuggest,
      throttledAddOrUpdateAddress,
      handleUuid,
    });

  const handleAddNewPerson = async () => {
    //create them then add them to the incident
    //will need to either look up addresses or create new addresses
    let input = {
      personId: emptyPis?.person?.id,
      originatorId: user?.id,
      firstName: personObj?.["firstName"]
        .split(" ")
        .map(i => _.capitalize(i))
        .join(" ")
        .trim(),
      middleName: _.capitalize(personObj?.["middleName"])
        .split(" ")
        .map(i => _.capitalize(i))
        .join(" ")
        .trim(),
      lastName: _.capitalize(personObj?.["lastName"])
        .split(" ")
        .map(i => _.capitalize(i))
        .join(" ")
        .split("-")
        .map(i => _.capitalize(i))
        .join("-")
        .trim(),
      suffix: _.capitalize(personObj?.["nameSuffix"])
        .split(" ")
        .map(i => _.capitalize(i))
        .join(" ")
        .trim(),
      juvenile: isJuvenile(moment(personObj?.["dob"]), "MMDDYYYY", moment()),
      organization: false,
      nibrsGenderId: +personObj?.["gender"] < 3 ? personObj?.["gender"] : 3,
      eyeColorId: eyeColors?.find(e =>
        e?.name?.startsWith(EYE_COLOR_CODES[personObj?.["eyeColor"]]),
      )?.id,
      hairColorId: hairColors?.find(e =>
        e?.name?.startsWith(HAIR_COLOR_CODES[personObj?.["hairColor"]]),
      )?.id,
      genderId: +personObj?.["gender"] < 3 ? personObj?.["gender"] : 3,
      licenseStateId: states?.find(s =>
        s?.name?.startsWith(personObj?.["state"]),
      )?.id,
      raceId: races?.find(r =>
        r?.name?.startsWith(RACE_CODES[personObj?.["race"]]),
      )?.id,
      ibrArresteeRaceTypeId:
        personObj?.["race"] === "H"
          ? ibrArresteeRaceTypes?.find(r => r?.name?.startsWith("Hispanic"))?.id
          : personObj?.["race"] === "U"
            ? ibrArresteeRaceTypes?.find(r => r?.name?.startsWith("Unknown"))
                ?.id
            : personObj?.["race"]
              ? ibrArresteeRaceTypes?.find(r => r?.name?.startsWith("Not"))?.id
              : null,
      dob: formatDob(personObj["dob"], personObj["iin"]?.country),
      driversLicense: personObj?.["licenseNumber"].replace(/\s/g, ""),
      height: convertHeight(personObj?.["height"]),
      weight:
        +convertWeight(
          personObj?.["weight"] || personObj?.["weightKgs"],
          personObj["iin"]?.country,
        ) ?? null,
      aliasName: formatName(
        personObj?.["firstNameAlias"],
        personObj?.["lastNameAlias"],
        personObj?.["suffixAlias"],
      ),
      incidentId: incidentId ? incidentId : null,
    };
    const homeAddressStr = formatAddressForAutoSuggest(
      personObj?.["street"],
      personObj?.["city"],
      personObj?.["state"],
      personObj?.["zip"],
    );
    const nameForQuery = formatName(
      input.firstName,
      input.middleName.substring(0, 1),
      input.lastName,
    );
    const genderForQuery =
      +personObj?.["gender"] === 1
        ? "M"
        : +personObj?.["gender"] === 2
          ? "F"
          : null;
    const res =
      onlineState &&
      (await getAddressAutoSuggest({
        originatorId: user?.id,
        input: homeAddressStr,
        count: 5,
      }));

    let addressArr;
    let streetArr;
    let streetNumber;
    let streetName;
    let city;
    let state;
    let zip;

    if (
      !res?.data?.addressAutoSuggest ||
      res?.data?.addressAutoSuggest?.length === 0
    ) {
      addressArr = homeAddressStr?.split(",").map(i => i.trim());
      streetArr = addressArr?.[0].split(" ");
      streetNumber = streetArr.shift();
      streetName = streetArr.join(" ");
      city = addressArr[1]
        .split(" ")
        .map(i => _.capitalize(i))
        .join(" ");
      state = addressArr[2];
      zip = addressArr[3];
      const addressUuid = handleUuid();
      let addressRes;

      if (onlineState) {
        addressRes = await throttledAddOrUpdateAddress({
          variables: {
            input: {
              originatorId: user?.id,
              id: addressUuid,
              streetNumber: streetNumber,
              streetName: streetName,
              city: city,
              state: state,
              zip: zip,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
        input["homeAddressId"] =
          addressRes?.data?.addOrUpdateAddress?.address?.id;
      } else {
        throttledAddOrUpdateAddress({
          variables: {
            input: {
              originatorId: user?.id,
              id: addressUuid,
              streetNumber: streetNumber,
              streetName: streetName,
              city: city,
              state: state,
              zip: zip,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
        input["homeAddressId"] = addressUuid;
      }
    } else {
      input["homeAddressId"] = res?.data?.addressAutoSuggest?.[0]?.id;
    }
    //await addPerson then use the person id to add them to the incident
    if (onlineState) {
      const newPersonRes = await throttledAddOrUpdatePerson({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      addOrUpdatePersonInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            personId: newPersonRes?.data?.addOrUpdatePerson?.person?.id,
            personIncidentSpecificId: emptyPis?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    } else {
      throttledAddOrUpdatePerson({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      addOrUpdatePersonInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            personId: input?.personId,
            personIncidentSpecificId: emptyPis?.id,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      let pisClone = _.cloneDeep(emptyPis) || {};
      pisClone.person = {
        __typename: "Person",
        id: input.personId,
        uuid: input.personId,
        validPersonAlerts: null,
        personalAttribute: {
          __typename: "PersonalAttribute",
          dob: input.dob,
          firstName: input.firstName,
          homeAddress: {
            id: input.homeAddressId,
            name: homeAddressStr,
            city: city,
            state: state,
            streetName: streetName,
            streetNumber: streetNumber,
            unit: "",
            uuid: input.homeAddressId,
            validAddressAlerts: [],
            zip: zip,
          },
          id: input.personId,
          lastName: input.lastName,
          middleName: input.middleName,
          organization: input.organization,
          suffix: input.suffix,
          juvenile: input.juvenile,
          nibrsGender: nibrsGenders?.find(
            g => g?.id?.toString() === input?.nibrsGenderId?.toString(),
          ),
          gender: genders?.find(
            g => g?.id?.toString() === input.genderId?.toString(),
          ),
          eyeColor: eyeColors?.find(e =>
            e?.name?.startsWith(EYE_COLOR_CODES[personObj?.["eyeColor"]]),
          ),
          hairColor: hairColors?.find(e =>
            e?.name?.startsWith(HAIR_COLOR_CODES[personObj?.["hairColor"]]),
          ),
          driversLicense: input.driversLicense,
          licenseState: states?.find(s =>
            s?.name?.startsWith(personObj?.["state"]),
          ),
          race: races?.find(r =>
            r?.name?.startsWith(RACE_CODES[personObj?.["race"]]),
          ),
          ibrArresteeRaceType: {
            id: input.ibrArresteeRaceTypeId,
          },
          height: +input.height,
          weight: +input.weight,
          aliasName: input.aliasName,
        },
      };
      incidentDispatch({
        type: ADD_OR_UPDATE_PIS,
        payload: {
          incidentId: incidentId,
          pis: pisClone,
        },
      });
    }
    if (canRunQuery) {
      //if it's a Canadian license, run a UQ
      if (PROVINCES.includes(personObj?.["state"])) {
        runUq(
          input.dob.split("-").join(""),
          personObj?.["state"],
          nameForQuery,
          genderForQuery,
        );
      } else {
        runSnq(
          input.dob.split("-").join(""),
          personObj?.["state"],
          nameForQuery,
          genderForQuery,
        );
      }
    }
    handleClose();
  };

  const handleSelectedAdd = async () => {
    const input = {
      incidentId: incidentId,
      personId: selectedPerson?.id,
      personIncidentSpecificId: emptyPis?.id,
      originatorId: user?.id,
    };
    const personAlreadyOnIncident = incidentPeople.some(
      p => p.id === selectedPerson?.id,
    );
    if (personAlreadyOnIncident) {
      alert("This person is already on the incident");
      return;
    }
    //take the mergeChoice object, remove the "match" key value pairs, rename the keys with SCAN_TO_PERSON_KEY_CONVERSION, format the values
    if (Object.keys(mergeChoice).length > 0) {
      let inputObj = Object.fromEntries(
        Object.entries(mergeChoice).map(([k, v]) => [
          SCAN_TO_PERSON_KEY_CONVERSION[k],
          v,
        ]),
      );
      // address
      if (_.has(inputObj, "eyeColor")) {
        inputObj["eyeColorId"] =
          eyeColors?.find(e =>
            e?.name?.startsWith(EYE_COLOR_CODES[inputObj?.["eyeColor"]]),
          )?.id || "";
        delete inputObj.eyeColor;
      }
      if (_.has(inputObj, "hairColor")) {
        inputObj["hairColorId"] =
          hairColors?.find(e =>
            e?.name?.startsWith(HAIR_COLOR_CODES[inputObj?.["hairColor"]]),
          )?.id || "";
        delete inputObj.hairColor;
      }
      if (_.has(inputObj, "licenseState")) {
        inputObj["licenseStateId"] =
          states?.find(s => s?.name?.startsWith(inputObj?.["licenseState"]))
            ?.id || "";
        delete inputObj.licenseState;
      }
      if (_.has(inputObj, "dob")) {
        inputObj["dob"] =
          formatDob(inputObj["dob"], personObj["iin"]?.country) || "";
      }
      if (_.has(inputObj, "homeAddress")) {
        inputObj["homeAddressId"] =
          (await findOrCreateAddress(inputObj["homeAddress"])) || "";
        delete inputObj["homeAddress"];
      }
      if (_.has(inputObj, "gender")) {
        inputObj["genderId"] = GENDER_TO_ID[inputObj["gender"]] || "";
        delete inputObj["gender"];
      }

      let personUpdateInput = {
        personId: selectedPerson?.id,
        originatorId: user?.id,
        incidentId: incidentId ? incidentId : null,
      };
      personUpdateInput = { ...personUpdateInput, ...inputObj };
      addOrUpdatePerson({
        variables: { input: personUpdateInput },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
    if (input.personId) {
      addOrUpdatePersonInIncident({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      // const person = peopleSearchData?.searchPeople?.find(p => p.id === selectedPersonId)
      const person = selectedPerson;
      const dob = person?.personalAttribute?.dob
        ?.substring(0, 10)
        .split("-")
        .join("");
      const state = person?.personalAttribute?.licenseState?.name;
      const name = formatName(
        person?.personalAttribute?.firstName,
        person?.personalAttribute?.middleName?.substring(0, 1),
        person?.personalAttribute?.lastName,
      )?.trim();
      const gender = person?.personalAttribute?.gender?.name?.substring(0, 1);
      if (canRunQuery) {
        //if it's a Canadian license, run a UQ
        if (PROVINCES.includes(state)) {
          runUq(dob, state, name, gender);
        } else {
          runSnq(dob, state, name, gender);
        }
      }
      handleClose();
    } else {
      alert("No person selected");
    }
    return;
  };

  const handleClose = () => {
    clearInput();
    setModalOpen(false);
  };

  const checkSwitchAccess = () => {
    if (tokenDisabled) {
      alert(
        "Switch access has been blocked due to the SoftToken requirement being disabled.",
      );
      return false;
    } else {
      return true;
    }
  };

  const runSnq = (dob, state, name, gender) => {
    let input = {
      queryKey: "SNQ",
      originatorId: user?.id,
      incidentId: incidentId,
      PWD: user?.officer?.deptAttribute?.nletsPassword,
      DAC: user?.officer?.deptAttribute?.nletsDevId?.toString(),
      ORI: user?.officer?.deptAttribute?.nletsOri,
      USR: user?.officer?.deptAttribute?.nletsLogin,
      //either be the selected person or the license scan
      DOB: dob,
      DST: state,
      NAM: name,
      SEX: gender,
    };
    const allowQuery = checkSwitchAccess();
    if (allowQuery) {
      sendQueryRequest({
        variables: { input: input },
        context: { serializationKey: "incidentQueue" },
      });
    }
  };

  const runUq = (dob, state, name, gender) => {
    let input = {
      queryKey: "UQ",
      originatorId: user?.id,
      incidentId: incidentId,
      PWD: user?.officer?.deptAttribute?.nletsPassword,
      DAC: user?.officer?.deptAttribute?.nletsDevId?.toString(),
      ORI: user?.officer?.deptAttribute?.nletsOri,
      USR: user?.officer?.deptAttribute?.nletsLogin,
      //either be the selected person or the license scan
      DST: state,
      NAM: name,
      DOB: dob,
      SEX: gender,
      RSN: reasonCode?.name,
      PPS: propInSight?.name,
    };
    const allowQuery = checkSwitchAccess();
    if (allowQuery) {
      sendQueryRequest({
        variables: { input: input },
        context: { serializationKey: "incidentQueue" },
      });
    }
  };

  const inputRef = useRef(null);

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setData("");
    setPersonObj(null);
    setIsParsing(false);
    setSubmitted(false);
    setParsingError(false);
    setWait(false);
    setCombinedPersonSearchResults(null);
    setSelectedPerson(null);
    setMergeChoice({});
  };

  const validPerson = person => {
    const validationObj = {
      lastName: person?.lastName?.length > 0,
      firstName: person?.firstName?.length > 0,
      dob: person?.dob?.length === 8,
      licenseNumber: !!person?.licenseNumber,
      state: !!person?.state,
      address: isAddressComplete({
        streetName: person?.street,
        state: person?.state,
        zip: person?.zip,
        city: person?.city,
      }),
    };
    return !Object.keys(validationObj)
      .map(key => validationObj[key])
      .includes(false);
  };

  const disabledForCanadianInput = () => {
    const isCanadian = PROVINCES.includes(personObj?.["state"]);
    return isCanadian && canRunQuery ? !reasonCode || !propInSight : false;
  };

  return (
    <Modal
      open={modalOpen}
      onClose={() => handleClose()}
      onKeyDown={e => e.key === "Escape" && handleClose()}
      className={classes.modal}
    >
      <div
        className={classes.paper}
        ref={dashRef}
        style={{ padding: "0px", overflowY: "inherit" }}
      >
        <Accordion
          square
          expanded={showResults}
          onChange={() => setShowResults(!showResults)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <AccordionSummary
            style={{
              backgroundColor: nightMode ? "#616161" : "#fae8ad",
              margin: 0,
            }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={memoizedClasses.expansionPanelTypography}
                data-cy="initial-call-information-panel-header"
              >
                License Scan
                {showResults ? (
                  <ExpandMoreIcon data-testid="expand-more-icon" />
                ) : (
                  <ExpandLessIcon data-testid="expand-less-icon" />
                )}
              </Typography>
              <HelpIconButton objectname="mobile_license_scan" />
            </Box>
          </AccordionSummary>
          <AccordionDetails onClick={e => e.stopPropagation()}>
            <div className={classes.licenseScanModal}>
              <ScanInput
                id={"license-scan-input"}
                inputRef={inputRef}
                inputDisabled={!!personObj}
                submitDisabled={data === "" || !!personObj || isParsing}
                clearDisabled={data === "" || isParsing}
                submitCallback={handleSubmit}
                submitted={submitted}
                onClearCallback={clearInput}
                keyData={data}
                setKeyData={setData}
                decoding={isParsing}
                error={(!!personObj && !validPerson(personObj)) || parsingError}
                incidentId={incidentId}
                user={user}
                wait={wait}
                setWait={setWait}
              />
              {personObj && (
                <div>
                  <h3 style={{ margin: "10px" }}>Scan Result:</h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>
                        Name:{" "}
                        <b>
                          {personObj["lastName"]}, {personObj["firstName"]}{" "}
                          {personObj["middleName"]}
                        </b>
                      </span>
                      <span>
                        DOB: <b>{formatDobForDisplay(personObj["dob"])}</b>
                      </span>
                      <span>
                        Address:{" "}
                        <b>
                          {personObj["street"]} {personObj["city"]},{" "}
                          {personObj["state"]} {personObj["zip"]}
                        </b>
                      </span>
                      <span>
                        License Number: <b>{personObj["licenseNumber"]}</b>{" "}
                        License State: <b>{personObj["state"]}</b>
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>
                        Weight:{" "}
                        <b>
                          {convertWeight(
                            personObj?.["weight"],
                            personObj?.["country"],
                          )}
                          {personObj?.["weight"] &&
                            personObj?.["country"] &&
                            " lbs"}
                        </b>
                      </span>
                      <span>
                        Height:{" "}
                        <b>{convertHeight(personObj?.["height"], true)}</b>
                      </span>
                      <span>
                        Eye Color:{" "}
                        <b>{EYE_COLOR_CODES[personObj?.["eyeColor"]]}</b>
                      </span>
                      <span>
                        Gender: <b>{GENDER_CODES[+personObj?.["gender"]]}</b>
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <DisplayMatches
                personObj={personObj}
                user={user}
                selectedPerson={selectedPerson}
                setSelectedPerson={setSelectedPerson}
                scanDone={!!data && !wait}
                onlineState={onlineState}
                mergeChoice={mergeChoice}
                setMergeChoice={setMergeChoice}
                combinedPersonSearchResults={combinedPersonSearchResults}
                setCombinedPersonSearchResults={setCombinedPersonSearchResults}
              />
              <DisplayUqOptions
                reasonCode={reasonCode}
                setReasonCode={setReasonCode}
                propInSight={propInSight}
                setPropInSight={setPropInSight}
                isCanadian={PROVINCES.includes(personObj?.["state"])}
                canRunQuery={canRunQuery}
              />
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-around"
                style={{ width: "100%" }}
              >
                {/* Add a check if canadian and can run query then the dropdowns need to be filled */}
                <Button
                  disabled={
                    !personObj ||
                    !!selectedPerson ||
                    !validPerson(personObj) ||
                    disabledForCanadianInput()
                  }
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleAddNewPerson}
                >
                  Create New Person
                </Button>
                <Button
                  disabled={!selectedPerson || disabledForCanadianInput()}
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleSelectedAdd}
                >
                  Use Existing Person
                </Button>
                <Button
                  style={{
                    width: "fit-content",
                    marginTop: "1vh",
                    marginBottom: "3vh",
                    marginRight: "2vw",
                  }}
                  variant="contained"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </Box>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </Modal>
  );
};

export default LicenseScanModal;

const DisplayMatches = ({
  personObj,
  user,
  selectedPerson,
  setSelectedPerson,
  scanDone,
  onlineState,
  mergeChoice,
  setMergeChoice,
  combinedPersonSearchResults = null,
  setCombinedPersonSearchResults,
}) => {
  const [
    searchPeople,
    { loading: peopleSearchLoading, data: peopleSearchData },
  ] = useLazyQuery(PEOPLE_SEARCH_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });
  const [
    searchPeopleByPid,
    { loading: peopleSearchLoadingByPid, data: peopleSearchDataByPid },
  ] = useLazyQuery(PEOPLE_SEARCH_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  useEffect(() => {
    //if the personObj has a last name, first name, and dob do a personSearch and display the results
    if (
      personObj &&
      personObj.lastName &&
      personObj.firstName &&
      personObj.dob &&
      onlineState
    ) {
      let searchState = {
        personal_attribute_attributes: {
          organization: "0",
          last_name: personObj.lastName,
          first_name: personObj.firstName,
          dob_search_start_date: formatDobForSearch(personObj.dob),
          juvenile: "0",
          drivers_license: "",
          license_state_id: null,
          phone: "",
          home_address_attributes: {
            street_number_start: "",
            street_number_end: "",
            street_name: "",
            city: "",
          },
        },
      };
      searchPeople({
        variables: { perPage: 10, page: 1, json: JSON.stringify(searchState) },
      });
    }
    if (personObj && personObj.licenseNumber && onlineState) {
      let pidSearchState = {
        personal_attribute_attributes: {
          organization: "0",
          last_name: "",
          first_name: "",
          dob_search_start_date: "",
          juvenile: "0",
          drivers_license: personObj.licenseNumber,
          license_state_id: null,
          phone: "",
          home_address_attributes: {
            street_number_start: "",
            street_number_end: "",
            street_name: "",
            city: "",
          },
        },
      };
      searchPeopleByPid({
        variables: {
          perPage: 10,
          page: 1,
          json: JSON.stringify(pidSearchState),
        },
      });
    }
    // These queries should not be included as GraphQL creates new functions on each call but they don't functionally change between calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personObj, onlineState]);

  useEffect(() => {
    if (peopleSearchData && peopleSearchDataByPid) {
      const mergedArr = peopleSearchData?.searchPeople.concat(
        peopleSearchDataByPid?.searchPeople,
      );
      const uniqueArr = _.uniqBy(mergedArr, "id");
      setCombinedPersonSearchResults(uniqueArr);
    }
  }, [peopleSearchData, peopleSearchDataByPid, setCombinedPersonSearchResults]);

  return (
    <div>
      <br />
      {scanDone && !window.navigator.onLine && (
        <p>You are currently offline. Matches cannot be displayed.</p>
      )}
      {scanDone &&
        (!!combinedPersonSearchResults ||
          peopleSearchLoading ||
          peopleSearchLoadingByPid) && (
          <h3 style={{ margin: "10px" }}>Search Results:</h3>
        )}
      {scanDone && (peopleSearchLoading || peopleSearchLoadingByPid) && (
        <p>Loading matches...</p>
      )}
      {scanDone &&
        combinedPersonSearchResults &&
        combinedPersonSearchResults?.length === 0 && <p>No matches found</p>}
      {scanDone &&
        combinedPersonSearchResults &&
        combinedPersonSearchResults?.length > 0 &&
        combinedPersonSearchResults.map((person, index) => {
          return (
            <MatchedName
              key={person?.id + "-" + index + "-nameDob"}
              person={person}
              index={index}
              selectedPerson={selectedPerson}
              setSelectedPerson={setSelectedPerson}
            />
          );
        })}
      <br />
      {!!personObj && !!selectedPerson && (
        <Conflicts
          mergeChoice={mergeChoice}
          setMergeChoice={setMergeChoice}
          scanResult={personObj}
          matchResult={selectedPerson}
        />
      )}
    </div>
  );
};

const MatchedName = ({ person, index, selectedPerson, setSelectedPerson }) => {
  return (
    <div
      key={person?.id}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginLeft: "1vw",
      }}
    >
      {/* <input type="checkbox" onChange={() => setSelectedPersonId(person.id)} /> */}
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedPerson?.id === person?.id}
              onChange={() =>
                setSelectedPerson(prevState =>
                  prevState?.id === person.id ? null : person,
                )
              }
              color="primary"
              data-cy={"match-checkbox-" + index}
              inputProps={{ "data-cy": index + "-checkbox-input" }}
            />
          }
        />
      </FormControl>
      <span style={{ color: personAlertColor(person) }} key={person.id}>
        {person.personalAttribute?.lastName && (
          <>
            Name:&nbsp;
            <b>
              {person.personalAttribute?.lastName},{" "}
              {person.personalAttribute?.firstName}{" "}
              {person.personalAttribute?.middleName}
            </b>
            &nbsp;&nbsp;
          </>
        )}
        {person?.personalAttribute?.dob && (
          <>
            DOB:&nbsp;
            <b>
              {moment
                .utc(person?.personalAttribute?.dob, "YYYY-MM-DD HH:mm:ss UTC")
                .local()
                .format("MM/DD/YYYY")}
            </b>
            &nbsp;&nbsp;
          </>
        )}
        {person?.personalAttribute?.gender && (
          <>
            Gender:&nbsp;<b>{person.personalAttribute?.gender?.name}</b>
            &nbsp;&nbsp;
          </>
        )}
        {person?.personalAttribute?.driversLicense && (
          <>
            License Number:&nbsp;
            <b>{person.personalAttribute?.driversLicense}</b>&nbsp;&nbsp;
          </>
        )}
        {person?.personalAttribute?.licenseState && (
          <>
            License State:&nbsp;
            <b>{person.personalAttribute?.licenseState?.name}</b>
          </>
        )}
      </span>
    </div>
  );
};

const comparePeople = (a, b) => {
  return {
    lastName:
      a?.lastName?.toUpperCase()?.trim() ===
      b?.personalAttribute?.lastName?.toUpperCase()?.trim(),
    middleName:
      a?.middleName?.toUpperCase()?.trim() ===
      b?.personalAttribute?.middleName?.toUpperCase()?.trim(),
    firstName:
      a?.firstName?.toUpperCase()?.trim() ===
      b?.personalAttribute?.firstName?.toUpperCase()?.trim(),
    licenseNumber:
      a?.licenseNumber?.trim() === b?.personalAttribute?.driversLicense?.trim(),
    state:
      a?.state?.toUpperCase()?.trim() ===
      b?.personalAttribute?.licenseState?.name?.toUpperCase()?.trim(),
    dob:
      a?.dob?.trim() ===
      moment.utc(b?.personalAttribute?.dob).format("MMDDYYYY"),
    weight:
      a?.weight?.split(" ")[0]?.toString() ===
      b?.personalAttribute?.weight?.toString(),
    height: convertHeight(a?.height, false) === +b?.personalAttribute?.height,
    eyeColor: b?.personalAttribute?.eyeColor?.name?.startsWith(
      EYE_COLOR_CODES[a?.eyeColor],
    ),
    hairColor:
      (!b?.personalAttribute?.hairColor && !a?.hairColor) ||
      b?.personalAttribute?.hairColor?.name?.startsWith(
        HAIR_COLOR_CODES[a?.hairColor],
      ),
    address:
      formatAddressForAutoSuggest(
        a?.street,
        a?.city,
        a?.state,
        a?.zip,
      ).toUpperCase() ===
      b?.personalAttribute?.homeAddress?.name?.toUpperCase(),
    gender:
      a?.gender?.toString() === b?.personalAttribute?.gender?.id?.toString(),
  };
};

const INPUT_LABELS = {
  lastName: "Last\xa0Name",
  middleName: "Middle\xa0Name",
  firstName: "First\xa0Name",
  licenseNumber: "License\xa0Number",
  state: "License\xa0State",
  dob: "DOB",
  weight: "Weight",
  height: "Height",
  eyeColor: "Eye\xa0Color",
  hairColor: "Hair\xa0Color",
  address: "Home\xa0Address",
  gender: "Gender",
};

const SCAN_TO_PERSON_KEY_CONVERSION = {
  lastName: "lastName",
  middleName: "middleName",
  firstName: "firstName",
  licenseNumber: "driversLicense",
  state: "licenseState",
  dob: "dob",
  weight: "weight",
  height: "height",
  eyeColor: "eyeColor",
  hairColor: "hairColor",
  address: "homeAddress",
  gender: "gender",
};
const Conflicts = ({
  scanResult,
  matchResult,
  mergeChoice,
  setMergeChoice,
}) => {
  //compare each field
  //if they're different show the options in a dropdown

  const [comparison, setComparison] = useState();

  useEffect(() => {
    setComparison(comparePeople(scanResult, matchResult));
  }, [scanResult, matchResult]);

  const handleChoice = (e, property) => {
    let value = e.target.value;
    if (property === "weight") {
      value = +value;
    }
    setMergeChoice(prevState => {
      return { ...prevState, [property]: value };
    });
  };

  if (comparison && Object.values(comparison).includes(false)) {
    return (
      <div>
        <br />
        <h3>Conflicts:</h3>
        <p>
          We found differences between the license scan and your selected match.
                        Please use the drop-downs to select the correct values and we&apos;ll
          update the selected person accordingly.
        </p>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
          }}
        >
          {Object.keys(comparison).map(property => {
            if (!comparison[property]) {
              let matchValue = matchResult?.personalAttribute[
                SCAN_TO_PERSON_KEY_CONVERSION[property]
              ]?.id
                ? matchResult?.personalAttribute[
                    SCAN_TO_PERSON_KEY_CONVERSION[property]
                  ]?.name
                : matchResult?.personalAttribute[
                    SCAN_TO_PERSON_KEY_CONVERSION[property]
                  ];
              //currently not a valid comparison as we search based on name and DOB
              if (property === "dob") {
                matchValue = moment.utc(matchValue).format("MMDDYYYY");
              }
              let scanValue = scanResult[property];
              if (property === "weight") {
                scanValue = +convertWeight(scanValue, scanResult?.country);
                matchValue = +matchValue;
              }
              if (property === "height") {
                scanValue = convertHeight(scanValue);
              }
              if (property === "address") {
                scanValue = formatAddressForAutoSuggest(
                  scanResult?.street,
                  scanResult?.city,
                  scanResult?.state,
                  scanResult?.zip,
                );
              }
              if (property === "gender") {
                scanValue = GENDER_CODES[+scanResult?.gender];
                matchValue =
                  GENDER_CODES[+matchResult?.personalAttribute?.gender?.id];
              }
              if (
                property === "lastName" ||
                property === "middleName" ||
                property === "firstName"
              ) {
                scanValue = _.capitalize(scanValue);
              }
              return (
                <FormControl key={property}>
                  <InputLabel shrink={true} id="demo-simple-select-label">
                    {INPUT_LABELS[property]}
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    // value={mergeChoice[property] || "match"}
                    value={mergeChoice[property] ?? matchValue ?? ""}
                    onChange={e => handleChoice(e, property)}
                  >
                    {/* <MenuItem style={{fontStyle: "italic"}} value={""}>None</MenuItem> */}
                    <ListSubheader>Scan Result</ListSubheader>
                    {scanValue ? (
                      <MenuItem value={scanValue}>{scanValue}</MenuItem>
                    ) : (
                      <MenuItem value={""} style={{ fontStyle: "italic" }}>
                        None
                      </MenuItem>
                    )}
                    <ListSubheader>Existing Record</ListSubheader>
                    {/* {matchResult?.personalAttribute[SCAN_TO_PERSON_KEY_CONVERSION[property]] */}
                    {matchValue ? (
                      <MenuItem value={matchValue}>{matchValue}</MenuItem>
                    ) : (
                      <MenuItem value={""} style={{ fontStyle: "italic" }}>
                        None
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              );
            } else {
              return null;
            }
          })}
        </div>
      </div>
    );
  } else {
    return <div>No conflicts</div>;
  }
};

const DisplayUqOptions = ({
  reasonCode,
  setReasonCode,
  propInSight,
  setPropInSight,
  isCanadian,
  canRunQuery,
}) => {
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);
  return isCanadian && canRunQuery ? (
    <div>
      <h3 style={{ margin: "10px" }}>Required Query Fields:</h3>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
        }}
      >
        <SingleDropDown
          title={"Reason Code"}
          value={reasonCode}
          stateName={"reasonCode"}
          options={REASON_CODES}
          handleChange={e => setReasonCode(e.target.value)}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          disabled={false}
          name={"fullName"}
        />
        <SingleDropDown
          title={"Property In Sight"}
          value={propInSight}
          stateName={"propInSight"}
          options={PROP_IN_SIGHTS}
          handleChange={e => setPropInSight(e.target.value)}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          disabled={false}
          name={"fullName"}
        />
      </div>
      <br />
    </div>
  ) : null;
};

const PROP_IN_SIGHTS = [
  {
    id: 1,
    fullName: "Y - Yes",
    name: "Y",
  },
  {
    id: 2,
    fullName: "N - No",
    name: "N",
  },
];
const REASON_CODES = [
  {
    id: 1,
    fullName: "T - Traffic Violation",
    name: "T",
  },
  {
    id: 2,
    fullName: "N - Narcotics",
    name: "N",
  },
  {
    id: 3,
    fullName: "F - Fraud",
    name: "F",
  },
  {
    id: 4,
    fullName: "V - Violent Crime",
    name: "V",
  },
  {
    id: 5,
    fullName: "S - Theft",
    name: "S",
  },
  {
    id: 6,
    fullName: "H - Humanitarian",
    name: "H",
  },
];
